/**
 * The golden glow on the egg and nest while a golden egg's bonus runs
 * (docs/mockups/golden-glow-e5.html, S2 with E7's ending): a one-pixel gold
 * rim round the two together, a white highlight running round it (a lap
 * every two seconds, then a one-second rest), and glints twinkling about
 * them. Over the bonus's last three seconds the rim thins to every other
 * pixel, then every fourth, and the highlight and glints stop, so the end
 * is visible before it happens.
 *
 * The timing and the rim are pure so they can be tested; drawing takes the
 * yard's context and native pixels.
 */

const GOLD_LIGHT = '#fee761';
const GOLD = '#feae34';
const WHITE = '#ffffff';

/** Seconds for the highlight to go once round, and the pause before the next. */
export const GLOW_LAP_SECONDS = 2;
export const GLOW_REST_SECONDS = 1;
/** The last seconds of a bonus, over which the glow thins out. */
export const GLOW_FADE_SECONDS = 3;
/** Half the highlight's length, in radians round the rim. */
const HIGHLIGHT_HALF_ARC = 0.35;
/** The rim's angles are measured with x squashed by this: the egg is taller than wide. */
const ANGLE_X_SQUASH = 1.6;

export interface RimPoint {
  x: number;
  y: number;
  /** Angle round the rim's centre, -PI..PI, for the travelling highlight. */
  a: number;
}

/**
 * The pixels just outside an alpha mask (4-neighbour), with each one's angle
 * round (cx, cy). `alpha` is RGBA data `w` x `h`; a pixel counts as solid
 * past half opacity, as the sprite outlines do.
 */
export function rimPoints(
  alpha: Uint8ClampedArray,
  w: number,
  h: number,
  cx: number,
  cy: number,
): RimPoint[] {
  const solid = (x: number, y: number): boolean =>
    x >= 0 && y >= 0 && x < w && y < h && (alpha[(y * w + x) * 4 + 3] ?? 0) > 127;
  const out: RimPoint[] = [];
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      if (solid(x, y)) continue;
      if (solid(x - 1, y) || solid(x + 1, y) || solid(x, y - 1) || solid(x, y + 1)) {
        out.push({ x, y, a: Math.atan2(y - cy, (x - cx) / ANGLE_X_SQUASH) });
      }
    }
  }
  return out;
}

/**
 * How the glow looks with this long left on the bonus: every rim pixel
 * (`step` 1) with the highlight and glints, then every other and every
 * fourth pixel alone, then nothing.
 */
export function glowStage(secondsLeft: number): { step: number; lively: boolean } | null {
  if (secondsLeft <= 0) return null;
  if (secondsLeft > GLOW_FADE_SECONDS) return { step: 1, lively: true };
  return { step: secondsLeft > GLOW_FADE_SECONDS / 2 ? 2 : 4, lively: false };
}

/**
 * Where the highlight's middle is at time `t` (seconds), as an angle, or
 * null while it rests between laps. Each lap starts at the bottom and runs
 * up the left, over the top and back down.
 */
export function highlightAngle(t: number): number | null {
  const cycle = GLOW_LAP_SECONDS + GLOW_REST_SECONDS;
  const into = ((t % cycle) + cycle) % cycle;
  if (into >= GLOW_LAP_SECONDS) return null;
  return Math.PI / 2 + (into / GLOW_LAP_SECONDS) * Math.PI * 2;
}

/** Whether a rim angle is inside the highlight centred on `head`. */
export function inHighlight(a: number, head: number): boolean {
  const d = Math.abs(((((a - head) % (Math.PI * 2)) + Math.PI * 3) % (Math.PI * 2)) - Math.PI);
  return d < HIGHLIGHT_HALF_ARC;
}

export interface Glint {
  /** Offset from the egg's centre column and the nest's base row. */
  dx: number;
  dy: number;
  /** Seconds into its own eight-beat cycle it starts, so they don't twinkle together. */
  phase: number;
  white: boolean;
}

/** A fixed scatter of glints about the egg and nest; the same every time. */
export function makeGlints(count = 12, seed = 13): Glint[] {
  let s = seed;
  const rand = (): number => (s = (s * 9301 + 49297) % 233280) / 233280;
  return Array.from({ length: count }, () => ({
    dx: Math.round(-46 + rand() * 92),
    dy: Math.round(-88 + rand() * 82),
    phase: rand() * 20,
    white: rand() < 0.35,
  }));
}

/** One glint: a dot that grows to a plus, then a wider plus, then shrinks. */
function drawGlint(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  size: number,
  white: boolean,
): void {
  ctx.fillStyle = white ? WHITE : GOLD_LIGHT;
  ctx.fillRect(x, y, 1, 1);
  if (size >= 1) {
    ctx.fillRect(x - 1, y, 3, 1);
    ctx.fillRect(x, y - 1, 1, 3);
  }
  if (size >= 2) {
    ctx.fillStyle = GOLD;
    ctx.fillRect(x - 2, y, 1, 1);
    ctx.fillRect(x + 2, y, 1, 1);
    ctx.fillRect(x, y - 2, 1, 1);
    ctx.fillRect(x, y + 2, 1, 1);
  }
}

/**
 * Draws the glow in native pixels: the rim, then (while lively) the
 * highlight over it and the glints about. `cx`, `baseY` anchor the glints:
 * the egg's centre column and the nest's base row.
 */
export function drawGoldenGlow(
  ctx: CanvasRenderingContext2D,
  rim: readonly RimPoint[],
  glints: readonly Glint[],
  cx: number,
  baseY: number,
  t: number,
  secondsLeft: number,
): void {
  const stage = glowStage(secondsLeft);
  if (!stage) return;
  ctx.fillStyle = GOLD_LIGHT;
  for (const p of rim) if ((p.x + p.y) % stage.step === 0) ctx.fillRect(p.x, p.y, 1, 1);
  if (!stage.lively) return;
  const head = highlightAngle(t);
  if (head !== null) {
    ctx.fillStyle = WHITE;
    for (const p of rim) if (inHighlight(p.a, head)) ctx.fillRect(p.x, p.y, 1, 1);
  }
  for (const g of glints) {
    const life = (t * 1.3 + g.phase) % 8;
    if (life > 4) continue;
    const size = life < 1 || life > 3 ? 0 : life < 2 ? 1 : 2;
    drawGlint(ctx, cx + g.dx, baseY + g.dy, size, g.white);
  }
}
